// src/pages/PartnerRegister.js
import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useFormInput, onlyNumericInput } from "@/utils";
import { Confirm, Loading, Toast } from "@/components";
import SearchAddress from "@/components/Modal/Common/SearchAddress";

import {
    useGetAgentePartnerQuery,
    usePutAgentePartnerMutation,
    useDeleteAgentePartnerMutation,
    useGetAgentePartnerListQuery,
    usePostAgentePartnerMutation,
} from "@/redux/partnerApi";

export const PartnerRegister = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const { formData, handleChange, setFormData } = useFormInput({
        mode: "CREATE", // "CREATE" / "UPDATE"
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,
        id: null,

        // 소개처 필드
        name: "",
        phone: "",
        address: "",
        addressDetail: "",
        bCode: "",
        memo: "",
    });

    const { mode, toastMsg, confirmStatus, addressPopupStatus } = formData;

    // 1) 모드 설정: id 유무
    useEffect(() => {
        if (id) {
            setFormData((prev) => ({
                ...prev,
                mode: "UPDATE",
                id: id,
            }));
        }
    }, [id, setFormData]);

    // 2) UPDATE 모드일 경우, 소개처 상세조회
    const {
        data: data,
        isLoading: isDetailLoading,
        error: detailError,
    } = useGetAgentePartnerQuery(id, {
        skip: !id, // id 없으면 호출X
    });

    // 3) 상세조회 응답 → formData 매핑
    useEffect(() => {
        if (data && data.data) {
            const d = data.data;
            setFormData((prev) => ({
                ...prev,
                name: d.name || "",
                phone: d.phone || "",
                bCode: d.bCode || "",
                address: d.address || "",
                addressDetail: d.addressDetail || "",
            }));
        }
    }, [data, setFormData]);

    // 5) post / put mutation
    const [postAgentePartner, { isLoading: isPostLoading }] = usePostAgentePartnerMutation();
    const [putAgentePartner, { isLoading: isPutLoading }] = usePutAgentePartnerMutation();
    const [deleteAgentePartner, { isLoading: isDeleteLoading }] = useDeleteAgentePartnerMutation();

    // 6) 저장 Confirm
    const handleSaveConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            const requestBody = {
                name: formData.name,
                phone: formData.phone,
                address: formData.address,
                addressDetail: formData.addressDetail,
                bCode: formData.bCode,
                memo: formData.memo,
            };

            if (mode === "UPDATE") {
                // 수정
                await putAgentePartner({
                    id: formData.id,
                    body: requestBody,
                }).unwrap();
            } else {
                // 등록
                await postAgentePartner(requestBody).unwrap();
            }

            if (mode === "CREATE") {
                setFormData((prev) => ({
                    ...prev,
                    isLoading: false,
                    confirmStatus: {
                        confirmPopupStatus: true,
                        confirmTitle: "등록이 완료되었습니다.",
                        confirmDescription: "",
                        confirmButtons: [
                            {
                                label: "확인",
                                className: "btn-primary",
                                onClick: () => navigate("/admin/partner-management"),
                            },
                        ],
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: "수정이 완료되었습니다.",
                    isLoading: false,
                }));
            }
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "에러 발생",
            }));
        }
    };

    // 7) 저장 버튼
    const handleSave = () => {
        // 유효성 검사
        if (!formData.name) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "소개처명을 입력해주세요.",
            }));
            return;
        }

        if (!formData.phone) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "핸드폰번호를 입력해주세요.",
            }));
            return;
        }

        if (!formData.address) {
            setFormData((prev) => ({
                ...prev,
                toastMsg: "소개처 주소를 입력해주세요.",
            }));
            return;
        }

        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: `소개처 정보를 ${mode === "CREATE" ? "등록" : "수정"}하시겠습니까?`,
                confirmDescription: "",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleSaveConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    // 8) 삭제 Confirm (미구현)
    const handleDeleteConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: { confirmPopupStatus: false },
        }));

        try {
            await deleteAgentePartner(id).unwrap();

            setFormData((prev) => ({
                ...prev,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: "삭제 완료",
                    confirmDescription: "해당 소개처 정보가 삭제되었습니다.",
                    confirmButtons: [
                        {
                            label: "확인",
                            className: "btn-primary",
                            onClick: () => navigate("/admin/operator-management"),
                        },
                    ],
                },
            }));
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error.message || "삭제 중 오류가 발생했습니다.",
            }));
        }
    };

    // 9) 삭제 버튼
    const handleDelete = () => {
        setFormData((prev) => ({
            ...prev,
            confirmStatus: {
                confirmPopupStatus: true,
                confirmTitle: "해당 소개처 정보를 삭제하시겠습니까?",
                confirmDescription: "삭제 후에는 복구할 수 없습니다.",
                confirmButtons: [
                    { label: "예", className: "btn-primary", onClick: handleDeleteConfirm },
                    {
                        label: "아니오",
                        className: "btn-cancel",
                        onClick: () =>
                            setFormData((prev2) => ({
                                ...prev2,
                                confirmStatus: { confirmPopupStatus: false },
                            })),
                    },
                ],
            },
        }));
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="management-form container">
                <div className="form-container">
                    <div className="form-head">
                        <div className="head-tit">
                            <span className="form_name">소개처 등록</span>
                            <span className="form_desc"></span>
                        </div>
                        <div className="head-options">
                            {mode === "UPDATE" && (
                                <button type="button" className="btn btn-delete" onClick={handleDelete}>
                                    삭제
                                </button>
                            )}
                            <button type="button" className="btn btn-save" onClick={handleSave}>
                                저장
                            </button>
                        </div>
                    </div>

                    <div className="form-content">
                        <div className="form-wrapper">
                            <div className="form-item">
                                <div className="item-head">
                                    <span className="form-item_tit">소개처 정보</span>
                                    <div className="form-item_etc"></div>
                                </div>
                                <div className="item-body">
                                    <div className="item-list">
                                        <BusinessForm formData={formData} handleChange={handleChange} setFormData={setFormData} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Confirm 팝업 */}
            {formData.confirmStatus.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={formData.confirmStatus.confirmTitle}
                    description={formData.confirmStatus.confirmDescription}
                    buttons={formData.confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            address: data.address,
                            bCode: data.bcode,
                        }))
                    }
                />
            )}
        </>
    );
};

const BusinessForm = ({ formData, handleChange, setFormData }) => {
    const { name, phone, address, addressDetail, bCode, memo } = formData;

    return (
        <>
            {/* 소개처명 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>소개처명</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input type="text" name="name" placeholder="소개처명" value={name || ""} onChange={handleChange} />
                    </div>
                </div>
            </div>

            {/* 핸드폰번호 */}
            <div className="item-row" data-grow="3">
                <div className="row-head">
                    <span>핸드폰번호</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <input
                            type="text"
                            name="phone"
                            placeholder="핸드폰번호"
                            value={phone || ""}
                            onChange={handleChange}
                            onInput={onlyNumericInput}
                            maxLength={11}
                        />
                    </div>
                </div>
            </div>

            <div className="item-row" data-grow="3"></div>
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>소개처 주소</span>
                </div>
                <div className="row-content">
                    <div className="form-element" style={{ display: "none" }}>
                        <input
                            type="text"
                            name="bCode"
                            placeholder=""
                            value={bCode || ""}
                            onChange={handleChange}
                            readOnly
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                }))
                            }
                        />
                    </div>
                    <div className="form-element" style={{ width: "468px" }}>
                        <input
                            type="text"
                            name="address"
                            placeholder="주소"
                            value={address || ""}
                            onChange={handleChange}
                            readOnly
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                }))
                            }
                        />
                    </div>
                    <div className="form-element">
                        <input
                            type="text"
                            name="addressDetail"
                            placeholder="상세 주소"
                            value={addressDetail || ""}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="form-element_button">
                        <button
                            type="button"
                            className="btn-form_element btn-outline"
                            onClick={() =>
                                setFormData((prev) => ({
                                    ...prev,
                                    addressPopupStatus: true,
                                }))
                            }
                        >
                            주소검색
                        </button>
                    </div>
                </div>
            </div>

            {/* 메모 */}
            <div className="item-row" data-grow="1">
                <div className="row-head">
                    <span>메모</span>
                </div>
                <div className="row-content">
                    <div className="form-element">
                        <textarea name="memo" placeholder="메모를 작성해주세요." value={memo} onChange={handleChange} />
                    </div>
                </div>
            </div>
        </>
    );
};
